import type React from "react";
import {useEffect, useState} from "react";
import i18n from "../i18n";
import {SmsSenderPolicyCard} from "../components/sms-sender/SmsSenderPolicyCard";
import {useTranslation} from "react-i18next";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Autocomplete from "@mui/material/Autocomplete";
import {PageHeader} from "../components/ui/PageHeader";
import {StatusChip} from "../components/ui/StatusChip";
import {featureFlagLabel} from "../constants/featureFlagLabels";
import {summarizeOverrides} from "../utils/featureFlagResolution";
import {formatCount, formatEuroCents, formatStorageBytes} from "../util/format";
import {colors, fonts} from "../theme/tokens";
import {useAppDispatch, useAppSelector} from "../store/hooks";
import {fetchPlans, plansSelectors} from "../store/plansSlice";
import type {
    SelfSignupBookingSettings,
    SelfSignupDefaults,
    SelfSignupFeatureConfig,
    SelfSignupQuotas,
    SelfSignupSubscription,
    SmsProvider,
} from "../types/configuration";
import {getSelfSignupDefaults, updateSelfSignupDefaults} from "../api/configurationApi";
import {TernaryFeatureFlag} from "../components/feature-flags/TernaryFeatureFlag";

const COUNTRY_CODES = [
    {code: "AT", name: "Austria"},
    {code: "DE", name: "Germany"},
    {code: "CH", name: "Switzerland"},
    {code: "US", name: "United States"},
    {code: "GB", name: "United Kingdom"},
    {code: "FR", name: "France"},
    {code: "IT", name: "Italy"},
    {code: "ES", name: "Spain"},
    {code: "NL", name: "Netherlands"},
    {code: "BE", name: "Belgium"},
    {code: "PL", name: "Poland"},
    {code: "CZ", name: "Czech Republic"},
    {code: "HU", name: "Hungary"},
    {code: "RO", name: "Romania"},
    {code: "BG", name: "Bulgaria"},
    {code: "HR", name: "Croatia"},
    {code: "SI", name: "Slovenia"},
    {code: "SK", name: "Slovakia"},
    {code: "DK", name: "Denmark"},
    {code: "SE", name: "Sweden"},
    {code: "NO", name: "Norway"},
    {code: "FI", name: "Finland"},
    {code: "PT", name: "Portugal"},
    {code: "IE", name: "Ireland"},
    {code: "LU", name: "Luxembourg"},
    {code: "MT", name: "Malta"},
    {code: "CY", name: "Cyprus"},
    {code: "EE", name: "Estonia"},
    {code: "LV", name: "Latvia"},
    {code: "LT", name: "Lithuania"},
] as const;

const SELF_SIGNUP_FLAGS = [
    "fileUpload",
    "onlineBooking",
    "smsSending",
    "themePicker",
    "firstLaunch",
    "shiftSchedule",
    "precalculatedAvailabilities",
] as const;

const BOOKING_SWITCHES = [
    "showPrice",
    "showDuration",
    "allowBookingOnHolidays",
    "sendBookingConfirmationSMS",
    "customerEmailConfirmationRequired",
    "customerEmailReminder",
    "customerSMSReminder",
] as const;

/** What the signup backend applies while no rate limits are stored; shown instead of zeros, which mean something else. */
const BACKEND_RATE_LIMIT_DEFAULTS = {
    global: {limit: 5, windowHours: 1},
    perIp: {limit: 3, windowHours: 24},
} as const;

const DEFAULT_FORM_DATA: SelfSignupDefaults = {
    featureConfig: {
        fileUpload: true,
        onlineBooking: true,
        smsSending: true,
        themePicker: true,
        firstLaunch: false,
        shiftSchedule: null,
        precalculatedAvailabilities: null,
        smsProvider: "IBASIS",
    },
    subscription: {
        durationDays: 14,
        maxEmployees: 10,
        amountDueInCents: 0,
        amountPaidInCents: 0,
        planId: "DEMO_PLAN",
    },
    quotas: {
        storageBytes: 52428800,
        email: 10,
        sms: 0,
    },
    bookingSettings: {
        bookingAllowedInAdvanceDays: 30,
        bookingAllowedInAdvanceMinMinutes: 30,
        availabilityIntervalInMinutes: 30,
        showPrice: false,
        showDuration: false,
        allowBookingOnHolidays: false,
        sendBookingConfirmationSMS: false,
        customerEmailConfirmationRequired: false,
        customerEmailReminder: false,
        customerSMSReminder: false,
    },
    defaultCountryAlpha2: "AT",
};

export function SettingsPage() {
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const plans = useAppSelector(plansSelectors.selectAll);
    const plansStatus = useAppSelector((s) => s.plans.status);
    const [data, setData] = useState<SelfSignupDefaults | null>(null);
    const [formData, setFormData] = useState<SelfSignupDefaults>(DEFAULT_FORM_DATA);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const [expandedSections, setExpandedSections] = useState<Record<SectionKey, boolean>>({
        features: true,
        subscription: true,
        quotas: false,
        booking: false,
        country: false,
        rateLimiting: true,
    });

    const toggleSection = (section: SectionKey) => {
        setExpandedSections((prev) => ({...prev, [section]: !prev[section]}));
    };

    useEffect(() => {
        document.title = t("SETTINGS_PAGE_TITLE");
    }, [t]);

    useEffect(() => {
        if (plansStatus === "idle") void dispatch(fetchPlans());
    }, [dispatch, plansStatus]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await getSelfSignupDefaults();
                setData(result);
                setFormData(result);
            } catch (e) {
                const msg = e instanceof Error ? e.message : i18n.t("SETTINGS_LOAD_ERROR");
                setError(msg);
            } finally {
                setLoading(false);
            }
        };
        void loadData();
    }, []);

    const updateFeatureConfig = <K extends keyof SelfSignupFeatureConfig>(
        key: K,
        value: SelfSignupFeatureConfig[K],
    ) => {
        setFormData((prev) => ({
            ...prev,
            featureConfig: {...prev.featureConfig, [key]: value},
        }));
    };

    const updateSubscription = <K extends keyof SelfSignupSubscription>(
        key: K,
        value: SelfSignupSubscription[K],
    ) => {
        setFormData((prev) => ({
            ...prev,
            subscription: {...prev.subscription, [key]: value},
        }));
    };

    const updateQuotas = <K extends keyof SelfSignupQuotas>(key: K, value: SelfSignupQuotas[K]) => {
        setFormData((prev) => ({
            ...prev,
            quotas: {...prev.quotas, [key]: value},
        }));
    };

    const updateBookingSettings = <K extends keyof SelfSignupBookingSettings>(
        key: K,
        value: SelfSignupBookingSettings[K],
    ) => {
        setFormData((prev) => ({
            ...prev,
            bookingSettings: {...prev.bookingSettings, [key]: value},
        }));
    };

    const handleSaveClick = () => {
        setConfirmOpen(true);
    };

    const handleConfirmSave = async () => {
        setConfirmOpen(false);
        setSaving(true);
        setError(null);
        try {
            const result = await updateSelfSignupDefaults(formData);
            setData(result);
            setFormData(result);
            setSuccess(t("SETTINGS_SAVED"));
        } catch (e) {
            const msg = e instanceof Error ? e.message : t("SETTINGS_SAVE_ERROR");
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (data) {
            setFormData(data);
        }
    };

    const hasChanges = JSON.stringify(data) !== JSON.stringify(formData);

    const formatBytesToMB = (bytes: number): string => (bytes / 1048576).toString();
    const parseMBToBytes = (mb: string): number => Math.round(parseFloat(mb) * 1048576) || 0;

    const formatCentsToEur = (cents: number): string => (cents / 100).toFixed(2);
    const parseEurToCents = (eur: string): number => Math.round(parseFloat(eur) * 100) || 0;

    if (loading) {
        return (
            <Box data-testid="settings-loading" sx={{display: "flex", flexDirection: "column", gap: "14px"}}>
                <Skeleton variant="rounded" width={300} height={40} />
                {Array.from({length: 4}, (_, index) => (
                    <Skeleton key={index} variant="rounded" width="100%" height={64} />
                ))}
            </Box>
        );
    }

    const featureSummary = summarizeOverrides(formData.featureConfig, SELF_SIGNUP_FLAGS);
    const bookingSwitchesOn = BOOKING_SWITCHES.filter((setting) => formData.bookingSettings[setting]).length;
    const rateLimitsStored = Boolean(formData.rateLimiting?.global && formData.rateLimiting?.perIp);
    const globalLimit = formData.rateLimiting?.global?.limit ?? BACKEND_RATE_LIMIT_DEFAULTS.global.limit;
    const globalWindow = formData.rateLimiting?.global?.windowHours ?? BACKEND_RATE_LIMIT_DEFAULTS.global.windowHours;
    const perIpLimit = formData.rateLimiting?.perIp?.limit ?? BACKEND_RATE_LIMIT_DEFAULTS.perIp.limit;
    const perIpWindow = formData.rateLimiting?.perIp?.windowHours ?? BACKEND_RATE_LIMIT_DEFAULTS.perIp.windowHours;
    const setRateLimit = (scope: "global" | "perIp", change: Partial<{limit: number; windowHours: number}>) =>
        setFormData((prev) => ({
            ...prev,
            rateLimiting: {
                ...prev.rateLimiting,
                [scope]: {
                    limit: prev.rateLimiting?.[scope]?.limit ?? BACKEND_RATE_LIMIT_DEFAULTS[scope].limit,
                    windowHours: prev.rateLimiting?.[scope]?.windowHours ?? BACKEND_RATE_LIMIT_DEFAULTS[scope].windowHours,
                    ...change,
                },
            },
        }));
    const countryName = (code: string) => COUNTRY_CODES.find((country) => country.code === code)?.name ?? code;
    const planChoices = plans.map((plan) => plan.id);
    const changedSections = SECTION_KEYS.filter((key) => data && JSON.stringify(sectionValue(data, key)) !== JSON.stringify(sectionValue(formData, key)));

    const section = (key: SectionKey, title: string, summary: string, children: React.ReactNode) => (
        <Accordion
            expanded={expandedSections[key]}
            onChange={() => toggleSection(key)}
            slotProps={{transition: {unmountOnExit: false}}}
            data-testid={`settings-section-${key}`}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{px: 2, minHeight: 52, "& .MuiAccordionSummary-content": {my: 1.25, alignItems: "baseline", gap: 1.25, flexWrap: "wrap"}}}>
                <Typography variant="h6" component="h2">{title}</Typography>
                <Typography variant="body2" sx={{color: colors.muted}}>{summary}</Typography>
                {changedSections.includes(key) && <StatusChip tone="info">{t("SETTINGS_CHANGED")}</StatusChip>}
            </AccordionSummary>
            <AccordionDetails sx={{px: 2, pb: 2, pt: 2, borderTop: `1px solid ${colors.sectionDivider}`}}>{children}</AccordionDetails>
        </Accordion>
    );

    return (
        <Box data-testid="settings-page" sx={{display: "flex", flexDirection: "column", gap: "14px"}}>
            <PageHeader title={t("SETTINGS_TITLE")} subtitle={t("SETTINGS_SUBTITLE")} />

            {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {section("features", t("SETTINGS_SECTION_FEATURES"), t("SETTINGS_SUMMARY_FEATURES", {...featureSummary, provider: formData.featureConfig.smsProvider === "IBASIS" ? "IBASIS" : "AWS SNS"}), (
                <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", md: "1fr 1fr"}, columnGap: 4}}>
                    {SELF_SIGNUP_FLAGS.map((flag) => (
                        <Box key={flag} sx={{display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 33, borderBottom: `1px solid ${colors.rowDivider}`}}>
                            <Typography variant="body1" sx={{color: colors.ink}}>{featureFlagLabel(t, flag)}</Typography>
                            <TernaryFeatureFlag
                                value={formData.featureConfig[flag]}
                                onChange={(value) => updateFeatureConfig(flag, value)}
                                aria-label={featureFlagLabel(t, flag)}
                            />
                        </Box>
                    ))}
                    <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 40, borderBottom: `1px solid ${colors.rowDivider}`}}>
                        <Typography id="settings-sms-provider-label" variant="body1" sx={{color: colors.ink}}>{t("FEATURE_SMS_PROVIDER")}</Typography>
                        <Select
                            size="small"
                            value={formData.featureConfig.smsProvider}
                            onChange={(e) => updateFeatureConfig("smsProvider", e.target.value as SmsProvider)}
                            inputProps={{"aria-labelledby": "settings-sms-provider-label"}}
                            sx={{minWidth: 130}}
                        >
                            <MenuItem value="AWS_SNS">AWS SNS</MenuItem>
                            <MenuItem value="IBASIS">IBASIS</MenuItem>
                        </Select>
                    </Box>
                </Box>
            ))}

            {section("subscription", t("SETTINGS_SECTION_SUBSCRIPTION"), t("SETTINGS_SUMMARY_SUBSCRIPTION", {
                plan: formData.subscription.planId || "-",
                days: formData.subscription.durationDays,
                employees: formData.subscription.maxEmployees,
                amount: formatEuroCents(formData.subscription.amountDueInCents),
            }), (
                <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "repeat(2, 1fr)", lg: "2fr repeat(4, 1fr)"}, gap: 2}}>
                    <Autocomplete
                        freeSolo
                        options={planChoices}
                        getOptionLabel={(planId) => planId}
                        renderOption={(props, planId) => {
                            const {key, ...optionProps} = props;
                            return (
                                <Box component="li" key={key} {...optionProps} sx={{gap: 1}}>
                                    <Box component="span" sx={{fontFamily: fonts.mono, fontSize: "12px"}}>{planId}</Box>
                                    <Box component="span" sx={{color: colors.muted}}>{plans.find((plan) => plan.id === planId)?.name}</Box>
                                </Box>
                            );
                        }}
                        inputValue={formData.subscription.planId}
                        onInputChange={(_event, value) => updateSubscription("planId", value)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t("SETTINGS_PLAN_ID")}
                                size="small"
                                helperText={plans.find((plan) => plan.id === formData.subscription.planId)?.name ?? (plans.length > 0 ? t("SETTINGS_PLAN_NOT_IN_CATALOG") : undefined)}
                                inputProps={{...params.inputProps, style: {fontFamily: fonts.mono}}}
                            />
                        )}
                    />
                    <TextField
                        label={t("SETTINGS_DURATION_DAYS")}
                        type="number"
                        value={formData.subscription.durationDays}
                        onChange={(e) => updateSubscription("durationDays", parseInt(e.target.value, 10) || 0)}
                        size="small"
                        slotProps={{htmlInput: {min: 0}}}
                    />
                    <TextField
                        label={t("SETTINGS_MAX_EMPLOYEES")}
                        type="number"
                        value={formData.subscription.maxEmployees}
                        onChange={(e) => updateSubscription("maxEmployees", parseInt(e.target.value, 10) || 0)}
                        size="small"
                        slotProps={{htmlInput: {min: 0}}}
                    />
                    <TextField
                        label={t("SETTINGS_AMOUNT_DUE")}
                        type="number"
                        value={formatCentsToEur(formData.subscription.amountDueInCents)}
                        onChange={(e) => updateSubscription("amountDueInCents", parseEurToCents(e.target.value))}
                        size="small"
                        slotProps={{htmlInput: {min: 0, step: 0.01}}}
                    />
                    <TextField
                        label={t("SETTINGS_AMOUNT_PAID")}
                        type="number"
                        value={formatCentsToEur(formData.subscription.amountPaidInCents)}
                        onChange={(e) => updateSubscription("amountPaidInCents", parseEurToCents(e.target.value))}
                        size="small"
                        slotProps={{htmlInput: {min: 0, step: 0.01}}}
                    />
                </Box>
            ))}

            {section("quotas", t("SETTINGS_SECTION_QUOTAS"), t("SETTINGS_SUMMARY_QUOTAS", {
                storage: formatStorageBytes(formData.quotas.storageBytes),
                email: formatCount(formData.quotas.email),
                sms: formatCount(formData.quotas.sms),
            }), (
                <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "repeat(3, 1fr)"}, gap: 2}}>
                    <TextField
                        label={t("SETTINGS_STORAGE_MB")}
                        type="number"
                        value={formatBytesToMB(formData.quotas.storageBytes)}
                        onChange={(e) => updateQuotas("storageBytes", parseMBToBytes(e.target.value))}
                        size="small"
                        slotProps={{htmlInput: {min: 0, step: 1}}}
                    />
                    <TextField
                        label={t("DETAIL_QUOTA_EMAIL")}
                        type="number"
                        value={formData.quotas.email}
                        onChange={(e) => updateQuotas("email", parseInt(e.target.value, 10) || 0)}
                        size="small"
                        slotProps={{htmlInput: {min: 0}}}
                    />
                    <TextField
                        label={t("DETAIL_QUOTA_SMS")}
                        type="number"
                        value={formData.quotas.sms}
                        onChange={(e) => updateQuotas("sms", parseInt(e.target.value, 10) || 0)}
                        size="small"
                        slotProps={{htmlInput: {min: 0}}}
                    />
                </Box>
            ))}

            {section("booking", t("SETTINGS_SECTION_BOOKING"), t("SETTINGS_SUMMARY_BOOKING", {
                days: formData.bookingSettings.bookingAllowedInAdvanceDays,
                lead: formData.bookingSettings.bookingAllowedInAdvanceMinMinutes,
                interval: formData.bookingSettings.availabilityIntervalInMinutes,
                on: bookingSwitchesOn,
                total: BOOKING_SWITCHES.length,
            }), (
                <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                    <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "repeat(3, 1fr)"}, gap: 2}}>
                        <TextField
                            label={t("SETTINGS_BOOKING_ADVANCE_DAYS")}
                            type="number"
                            value={formData.bookingSettings.bookingAllowedInAdvanceDays}
                            onChange={(e) => updateBookingSettings("bookingAllowedInAdvanceDays", parseInt(e.target.value, 10) || 0)}
                            size="small"
                            slotProps={{htmlInput: {min: 0}}}
                        />
                        <TextField
                            label={t("SETTINGS_BOOKING_ADVANCE_MINUTES")}
                            type="number"
                            value={formData.bookingSettings.bookingAllowedInAdvanceMinMinutes}
                            onChange={(e) => updateBookingSettings("bookingAllowedInAdvanceMinMinutes", parseInt(e.target.value, 10) || 0)}
                            size="small"
                            slotProps={{htmlInput: {min: 0}}}
                        />
                        <TextField
                            label={t("SETTINGS_AVAILABILITY_INTERVAL")}
                            type="number"
                            value={formData.bookingSettings.availabilityIntervalInMinutes}
                            onChange={(e) => updateBookingSettings("availabilityIntervalInMinutes", parseInt(e.target.value, 10) || 0)}
                            size="small"
                            slotProps={{htmlInput: {min: 5, step: 5}}}
                        />
                    </Box>
                    <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", md: "1fr 1fr"}, columnGap: 4}}>
                        {BOOKING_SWITCHES.map((setting) => (
                            <FormControlLabel
                                key={setting}
                                sx={{m: 0, minHeight: 36, justifyContent: "space-between", flexDirection: "row-reverse", borderBottom: `1px solid ${colors.rowDivider}`}}
                                control={<Switch checked={formData.bookingSettings[setting]} onChange={(_, checked) => updateBookingSettings(setting, checked)} />}
                                label={t(`SETTINGS_${setting}`)}
                            />
                        ))}
                    </Box>
                </Box>
            ))}

            {section("country", t("SETTINGS_SECTION_COUNTRY"), `${countryName(formData.defaultCountryAlpha2)} (${formData.defaultCountryAlpha2}) · ${t("SETTINGS_SUMMARY_COUNTRY")}`, (
                <FormControl size="small" fullWidth sx={{maxWidth: 320}}>
                    <InputLabel id="settings-default-country-label">{t("SETTINGS_DEFAULT_COUNTRY")}</InputLabel>
                    <Select
                        labelId="settings-default-country-label"
                        value={formData.defaultCountryAlpha2}
                        label={t("SETTINGS_DEFAULT_COUNTRY")}
                        onChange={(e) => setFormData((prev) => ({...prev, defaultCountryAlpha2: e.target.value}))}
                    >
                        {COUNTRY_CODES.map(({code, name}) => (
                            <MenuItem key={code} value={code}>
                                {name} ({code})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            ))}

            {section("rateLimiting", t("SETTINGS_SECTION_RATE_LIMITING"), t("SETTINGS_SUMMARY_RATE_LIMITING", {
                global: globalLimit, globalWindow, perIp: perIpLimit, perIpWindow,
            }), (
                <Box sx={{display: "flex", flexDirection: "column", gap: 1.5}}>
                    <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)"}, gap: 2}}>
                        <TextField
                            label={t("SETTINGS_RATE_LIMIT_GLOBAL_LIMIT")}
                            type="number"
                            value={globalLimit}
                            onChange={(e) => setRateLimit("global", {limit: parseInt(e.target.value, 10) || 0})}
                            size="small"
                            slotProps={{htmlInput: {min: 0}}}
                        />
                        <TextField
                            label={t("SETTINGS_RATE_LIMIT_GLOBAL_WINDOW_HOURS")}
                            type="number"
                            value={globalWindow}
                            onChange={(e) => setRateLimit("global", {windowHours: parseInt(e.target.value, 10) || 1})}
                            size="small"
                            slotProps={{htmlInput: {min: 1}}}
                        />
                        <TextField
                            label={t("SETTINGS_RATE_LIMIT_PER_IP_LIMIT")}
                            type="number"
                            value={perIpLimit}
                            onChange={(e) => setRateLimit("perIp", {limit: parseInt(e.target.value, 10) || 0})}
                            size="small"
                            slotProps={{htmlInput: {min: 0}}}
                        />
                        <TextField
                            label={t("SETTINGS_RATE_LIMIT_PER_IP_WINDOW_HOURS")}
                            type="number"
                            value={perIpWindow}
                            onChange={(e) => setRateLimit("perIp", {windowHours: parseInt(e.target.value, 10) || 1})}
                            size="small"
                            slotProps={{htmlInput: {min: 1}}}
                        />
                    </Box>
                    <Box sx={{display: "flex", alignItems: "center", gap: 1, fontSize: "12.5px", color: colors.body}}>
                        <InfoIcon sx={{fontSize: 16, color: colors.warning}} />
                        {t("SETTINGS_RATE_LIMIT_SEMANTICS")}
                    </Box>
                    {!rateLimitsStored && (
                        <Typography variant="body2" sx={{color: colors.muted}}>{t("SETTINGS_RATE_LIMIT_BACKEND_DEFAULTS")}</Typography>
                    )}
                </Box>
            ))}

            <Box data-testid="settings-actions" sx={{display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap"}}>
                <Typography variant="body2" sx={{flex: 1, color: hasChanges ? colors.ink : colors.muted}}>
                    {hasChanges ? t("SETTINGS_UNSAVED_SECTIONS", {count: changedSections.length}) : t("SETTINGS_NO_UNSAVED_CHANGES")}
                </Typography>
                <Button variant="outlined" onClick={handleCancel} disabled={!hasChanges || saving}>
                    {t("CANCEL")}
                </Button>
                <Button variant="contained" onClick={handleSaveClick} disabled={!hasChanges || saving}>
                    {saving ? t("DETAIL_SAVING") : t("SETTINGS_REVIEW_AND_SAVE")}
                </Button>
            </Box>

            <SmsSenderPolicyCard />

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t("SETTINGS_CONFIRM_TITLE")}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{t("SETTINGS_CONFIRM_MESSAGE")}</DialogContentText>
                    {changedSections.length > 0 && (
                        <Box component="ul" sx={{mt: 1.5, mb: 0, pl: 2.5}}>
                            {changedSections.map((key) => (
                                <li key={key}>{t(SECTION_TITLE_KEYS[key])}</li>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => setConfirmOpen(false)}>{t("CANCEL")}</Button>
                    <Button onClick={() => void handleConfirmSave()} variant="contained" color="primary">
                        {t("DETAIL_SAVE")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={success != null}
                autoHideDuration={6000}
                onClose={() => setSuccess(null)}
                anchorOrigin={{vertical: "bottom", horizontal: "center"}}
            >
                <Alert severity="success" variant="filled" onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            </Snackbar>
        </Box>
    );
}

type SectionKey = "features" | "subscription" | "quotas" | "booking" | "country" | "rateLimiting";

const SECTION_KEYS: SectionKey[] = ["features", "subscription", "quotas", "booking", "country", "rateLimiting"];

const SECTION_TITLE_KEYS: Record<SectionKey, string> = {
    features: "SETTINGS_SECTION_FEATURES",
    subscription: "SETTINGS_SECTION_SUBSCRIPTION",
    quotas: "SETTINGS_SECTION_QUOTAS",
    booking: "SETTINGS_SECTION_BOOKING",
    country: "SETTINGS_SECTION_COUNTRY",
    rateLimiting: "SETTINGS_SECTION_RATE_LIMITING",
};

function sectionValue(defaults: SelfSignupDefaults, key: SectionKey): unknown {
    switch (key) {
        case "features":
            return defaults.featureConfig;
        case "subscription":
            return defaults.subscription;
        case "quotas":
            return defaults.quotas;
        case "booking":
            return defaults.bookingSettings;
        case "country":
            return defaults.defaultCountryAlpha2;
        case "rateLimiting":
            return defaults.rateLimiting ?? null;
    }
}
